import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr, resolveImg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import PhotoUploader from "../components/PhotoUploader";
import Countdown from "../components/Countdown";
import { toast } from "sonner";
import { ShieldAlert, Send, Megaphone } from "lucide-react";

const STAT = [
  { k: "active_auctions", l: "Active auctions" },
  { k: "pending_review", l: "Pending review" },
  { k: "sold_items", l: "Sold items" },
  { k: "total_orders", l: "Orders" },
];

export default function SellerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [listings, setListings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [active, setActive] = useState(null); // dispute
  const [response, setResponse] = useState({ body: "", attachments: [] });
  const [promoteListing, setPromoteListing] = useState(null);
  const [packages, setPackages] = useState([]);
  const [buyingPkg, setBuyingPkg] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/signin?next=/seller"); }, [user, loading, nav]);
  useEffect(() => {
    if (!user) return;
    reload();
    api.get("/promotions/packages").then(r => setPackages(r.data));
    // eslint-disable-next-line
  }, [user]);

  const buyPromotion = async (pkgKey) => {
    setBuyingPkg(pkgKey);
    try {
      await api.post(`/listings/${promoteListing.id}/promote`, { package_key: pkgKey });
      toast.success("Promotion active", { description: "Your listing is now boosted." });
      setPromoteListing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promotion failed"); }
    finally { setBuyingPkg(null); }
  };

  const reload = () => {
    api.get("/seller/stats").then(r => setStats(r.data));
    api.get("/listings", { params: { seller_id: user.id, status: null, limit: 50 } }).then(r => setListings(r.data));
    api.get("/disputes/mine", { params: { role: "seller" } }).then(r => setDisputes(r.data));
  };

  const openDispute = async (id) => {
    try { const r = await api.get(`/disputes/${id}`); setActive(r.data); setResponse({ body: "", attachments: [] }); }
    catch { toast.error("Cannot load dispute"); }
  };

  const submit = async () => {
    try {
      await api.post(`/disputes/${active.id}/message`, response);
      toast.success("Response submitted");
      const r = await api.get(`/disputes/${active.id}`);
      setActive(r.data);
      setResponse({ body: "", attachments: [] });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Seller Dashboard</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Welcome back, {user.name.split(" ")[0]}</h1>
        </div>
        <Link to="/sell" className="rounded-full px-4 py-2 bg-slate-900 text-white text-sm font-semibold">+ New Listing</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {STAT.map(s => (
          <div key={s.k} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="text-xs text-slate-500 uppercase font-bold tracking-wider">{s.l}</div>
            <div data-testid={`seller-stat-${s.k}`} className="text-3xl font-extrabold mt-1">{stats[s.k] ?? 0}</div>
          </div>
        ))}
        <div data-testid="seller-dashboard-metric-revenue" className="rounded-2xl bg-slate-900 text-white p-5 md:col-span-4">
          <div className="text-xs text-amber-400 uppercase font-bold tracking-wider">Estimated pending payout</div>
          <div className="text-4xl font-extrabold mt-1">{inr(stats.pending_payout)}</div>
          <div className="text-xs text-slate-400 mt-1">After 8% seller success fee. Released after buyer inspection.</div>
        </div>
      </div>

      <Tabs defaultValue="auctions">
        <TabsList>
          <TabsTrigger value="auctions">My Auctions ({listings.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="seller-tab-disputes">Disputes ({disputes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="auctions" className="mt-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">Current Bid</th><th className="px-4 py-3">Bids</th><th className="px-4 py-3">Time</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {listings.map(l => (
                  <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      <Link to={`/auction/${l.id}`} className="flex items-center gap-3">
                        {l.photos?.[0] && <img src={resolveImg(l.photos[0])} className="h-10 w-10 rounded-lg object-cover" alt="" />}
                        <span className="font-semibold line-clamp-1">{l.title}</span>
                        {l.promoted && <span className="text-[9px] uppercase font-black bg-amber-500 text-slate-900 px-1.5 py-0.5 rounded">Promoted</span>}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-bold">{inr(l.current_bid || l.starting_bid)}</td>
                    <td className="px-4 py-3">{l.bid_count}</td>
                    <td className="px-4 py-3">{l.status === "live" ? <Countdown endsAt={l.ends_at} small /> : "—"}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-md text-[10px] uppercase font-bold bg-slate-100 dark:bg-slate-800">{l.status}</span></td>
                    <td className="px-4 py-3">
                      {["live", "scheduled", "approved"].includes(l.status) && !l.promoted && (
                        <Button size="sm" variant="outline" data-testid={`promote-${l.id}`} className="h-8 gap-1 text-amber-600 border-amber-200"
                          onClick={() => setPromoteListing(l)}>
                          <Megaphone className="h-3.5 w-3.5" /> Promote
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {listings.length === 0 && <tr><td colSpan="6" className="text-center text-slate-500 py-10">No listings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="disputes" className="mt-6">
          {disputes.length === 0 ? <div className="text-slate-500 text-center py-12">No disputes.</div> : (
            <div className="space-y-3" data-testid="seller-disputes-list">
              {disputes.map(d => (
                <div key={d.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-center gap-4">
                  <ShieldAlert className={`h-5 w-5 ${d.status === "resolved" ? "text-emerald-600" : "text-red-600"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase font-bold text-amber-600 tracking-wider">Case #{d.id.slice(0, 8)}</div>
                    <div className="font-bold truncate">{d.listing_title}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Reason: {d.reason.replace(/_/g, " ")} · Buyer: {d.buyer_name} · Hold: {inr(d.payout_hold_amount)}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                    {d.status.replace(/_/g, " ")}
                  </span>
                  <Button size="sm" onClick={() => openDispute(d.id)} data-testid={`respond-${d.id}`}>
                    {d.status === "resolved" ? "View" : "Respond"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!promoteListing} onOpenChange={o => !o && setPromoteListing(null)}>
        <DialogContent className="max-w-2xl" data-testid="promote-modal">
          <DialogHeader>
            <DialogTitle>Promote listing — {promoteListing?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Paid promotion gives your auction extra visibility. It does <strong>not</strong> guarantee a sale, a price, or a number of bids.
          </p>
          <div className="grid md:grid-cols-3 gap-3 mt-4">
            {packages.map(p => (
              <div key={p.key} className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex flex-col">
                <div className="text-xs uppercase font-bold text-amber-600 tracking-wider">{p.name}</div>
                <div className="mt-1 text-3xl font-extrabold">{inr(p.price)}</div>
                <div className="text-xs text-slate-500">{p.duration_days} days</div>
                <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-400 flex-1">
                  {p.benefits.map((b) => <li key={b}>· {b}</li>)}
                </ul>
                <Button data-testid={`buy-${p.key}`} onClick={() => buyPromotion(p.key)}
                  disabled={buyingPkg === p.key}
                  className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
                  {buyingPkg === p.key ? "Processing…" : `Pay ${inr(p.price)} & Promote`}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={o => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="seller-dispute-modal">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>Dispute #{active.id.slice(0, 8)} — {active.status.replace(/_/g, " ")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                  <div className="text-xs font-bold uppercase text-slate-500">Buyer's complaint</div>
                  <div className="mt-1 text-sm"><strong>{active.reason.replace(/_/g, " ")}</strong></div>
                  <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">{active.description || "—"}</div>
                </div>
                {(active.buyer_evidence || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase text-slate-500 mb-2">Buyer evidence</div>
                    <div className="grid grid-cols-4 gap-2">
                      {active.buyer_evidence.map((p) => <img key={p} src={resolveImg(p)} className="aspect-square object-cover rounded-lg" alt="" />)}
                    </div>
                  </div>
                )}
                {active.messages?.length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase text-slate-500 mb-2">Timeline</div>
                    <div className="space-y-2">
                      {active.messages.map(m => (
                        <div key={m.id} className={`rounded-lg p-3 text-sm ${
                          m.author_role === "buyer" ? "bg-red-50 dark:bg-red-950/20"
                          : m.author_role === "seller" ? "bg-emerald-50 dark:bg-emerald-950/20"
                          : "bg-slate-50 dark:bg-slate-800"
                        }`}>
                          <div className="text-[10px] uppercase font-bold text-slate-500">{m.author_role} · {m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
                          <div className="mt-1">{m.body}</div>
                          {m.attachments?.length > 0 && (
                            <div className="mt-2 grid grid-cols-6 gap-1">{m.attachments.map((a) => <img key={a} src={resolveImg(a)} className="aspect-square object-cover rounded" alt="" />)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {active.resolution && (
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-sm text-emerald-800 dark:text-emerald-300">
                    <strong>Resolved:</strong> {active.resolution.replace(/_/g, " ")}
                    {active.resolution_note && <> — {active.resolution_note}</>}
                  </div>
                )}
                {active.status !== "resolved" && active.status !== "closed" && (
                  <div className="border-t border-slate-200 dark:border-slate-800 pt-4 space-y-3">
                    <div className="font-bold text-sm">Submit your response</div>
                    <Textarea rows={4} placeholder="Explain your side clearly. Stick to facts. Reference photos, tracking or documentation."
                      value={response.body} onChange={e => setResponse(r => ({ ...r, body: e.target.value }))}
                      data-testid="seller-response-body" />
                    <div>
                      <div className="text-xs text-slate-500 font-semibold mb-2">Evidence photos</div>
                      <PhotoUploader photos={response.attachments}
                        onChange={list => setResponse(r => ({ ...r, attachments: list }))} max={8} />
                    </div>
                    <div className="text-[11px] text-slate-500">Submitted evidence cannot be altered later. Add all supporting proof now.</div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
                {active.status !== "resolved" && active.status !== "closed" && (
                  <Button data-testid="seller-response-submit" disabled={!response.body} onClick={submit}
                    className="bg-slate-900 text-white gap-1">
                    <Send className="h-4 w-4" /> Submit response
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
